import { Component, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { LoaderService } from './shared/services/loader/loader.service';
import { LanguageService } from './shared/services/language/language.service';
import * as AppActions from './store/app/app.actions';
import { AppState } from './store/app/app.state';
import { WINDOW } from "./window.provider";
import * as AuthSelectors from './store/auth/auth.selectors';
import { take } from 'rxjs';
import { Router } from '@angular/router';
import { SessionRefreshService } from './shared/services/session-refresh.service';
import { PlatformService } from './shared/services/platform.service';
import { PushService } from './shared/services/push.service';
import { NetworkService } from './shared/services/network.service';
import { NotificationApiService } from './shared/services/notification-api.service';
import { BackButtonService } from './shared/services/back-button.service';
import { SheetGrabService } from './shared/services/sheet-grab.service';

@Component({
    selector: 'urban-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  private readonly store = inject(Store<AppState>);
  private readonly window = inject(WINDOW);
  private readonly language = inject(LanguageService);
  private readonly sessionRefresh = inject(SessionRefreshService);
  private readonly platformService = inject(PlatformService);
  private readonly push = inject(PushService);
  private readonly router = inject(Router);
  private readonly network = inject(NetworkService);
  private readonly notificationApi = inject(NotificationApiService);
  public readonly loaderService = inject(LoaderService);
  public readonly offlineBlocking = this.network.blocking;

  constructor() {
    // Created here, not on first use: the back button must be handled from
    // the login page on, and every dialog needs its sheet handle.
    inject(BackButtonService);
    inject(SheetGrabService);
    this.initializeApp();
    this.resumeWhenBackOnline();
  }

  // Blocked, the startup calls have failed one by one and nothing retries
  // them: a reload runs them all again in order. Otherwise only the bell needs
  // a push; SignalR reconnects by itself.
  private resumeWhenBackOnline(): void {
    this.network.backOnline$.subscribe(() => {
      if (this.network.blocking()) {
        this.window.location.reload();
      } else {
        this.notificationApi.triggerRefresh();
      }
    });
  }

  private initializeApp(): void {
    this.push.init();

    this.store.select(AuthSelectors.isAuth).pipe(take(1)).subscribe(isAuthenticated => {
      if (!isAuthenticated) {
        this.store.dispatch(AppActions.initializationStarted());
        return;
      }

      // Rehydrated session: no loginSuccess fires here, so the effect that
      // normally starts the timer never runs.
      this.sessionRefresh.start();
      this.push.start();

      // A reloaded browser reopens the deep URL, so the router lands where the
      // user was. A WebView always starts at '/', which redirects to the login
      // route: with a restored session there is nothing to log in to.
      if (this.platformService.isNative && !this.window.location.pathname.startsWith('/main')) {
        void this.router.navigate(['main']);
      }
    });

    this._setThemeOnStartup();
    this.language.init();
    this.store.dispatch(AppActions.getConfiguration());
    this._setIcons();
  }

  private _setThemeOnStartup(): void {
    this.store.dispatch(AppActions.updateHostname({
      hostname: this.window.location.hostname
    }));
  }

  private _setIcons(): void {
    this.store.dispatch(AppActions.initIcons());
  }
}
